<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const exampleCategories = [
  { name: 'Minuman Panas', color: '#E53935', icon: 'ri-cup-line', examples: 'Americano, Latte, Cappuccino, Teh Tarik' },
  { name: 'Minuman Dingin', color: '#1E88E5', icon: 'ri-cup-line', examples: 'Es Kopi Susu, Matcha Latte, Kopi Kocok' },
  { name: 'Makanan', color: '#43A047', icon: 'ri-restaurant-line', examples: 'Nasi Goreng, Mie Goreng, Sandwich' },
  { name: 'Snack', color: '#FB8C00', icon: 'ri-cake-line', examples: 'Roti Bakar, Kentang Goreng, Pisang Nugget' },
  { name: 'Paket', color: '#8E24AA', icon: 'ri-gift-line', examples: 'Paket Sarapan, Paket Meeting, Paket Couple' },
]

const faq = [
  {
    q: 'Berapa jumlah kategori yang bisa dibuat?',
    a: 'Tidak ada batasan jumlah kategori. Namun sebaiknya buat kategori secukupnya — 5 sampai 10 kategori sudah cukup untuk sebagian besar toko. Terlalu banyak kategori justru membuat tampilan kasir jadi ramai dan membingungkan.',
  },
  {
    q: 'Apakah kategori berlaku untuk produk saja, atau bahan baku juga?',
    a: 'Keduanya. Satu kategori bisa digunakan untuk produk (menu yang dijual ke pelanggan) sekaligus untuk bahan baku (ingredient). Misalnya kategori "Kopi" bisa dipakai untuk produk "Americano" dan juga bahan baku "Biji Kopi Arabika".',
  },
  {
    q: 'Saya salah input nama kategori. Bisa diedit?',
    a: 'Bisa. Klik ikon pensil (edit) di baris kategori yang ingin diubah. Ubah nama, lalu klik Update. Perubahan langsung berlaku — semua produk yang terhubung ke kategori ini otomatis terupdate juga.',
  },
  {
    q: 'Kategori sudah dipakai produk, bisa dihapus?',
    a: 'Tidak bisa dihapus. Sistem akan menampilkan pesan error "Kategori masih digunakan". Solusinya ada dua: (1) Pindahkan semua produk ke kategori lain dulu, atau (2) Matikan kategori dengan mengubah Status menjadi Nonaktif — kategori tidak akan muncul di kasir, tapi data tetap aman.',
  },
  {
    q: 'Apa fungsi angka di kolom "Urutan"?',
    a: 'Angka urutan menentukan posisi tampil kategori di halaman kasir dan daftar produk. Angka lebih kecil tampil lebih dulu. Contoh: Minuman Panas (urutan 1) tampil sebelum Makanan (urutan 3). Jika urutan sama, sistem mengurutkan berdasarkan nama secara alfabet.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <RouterLink
            to="/userguide/sistem/pangaturan"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            Settings
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Kategori</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="primary"
            variant="tonal"
            size="56"
            rounded="lg"
            class="me-3"
          >
            <VIcon
              icon="ri-price-tag-3-line"
              size="30"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">
              Manajemen Kategori
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelompokkan produk dan bahan baku Anda agar mudah ditemukan
            </p>
          </div>
        </div>

        <VAlert
          type="info"
          variant="tonal"
          icon="ri-flag-line"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda membuat, mengedit, dan mengatur kategori produk.
          Kategori wajib dibuat sebelum Anda menambahkan produk atau bahan baku.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-book-open-line"
              class="me-2"
              color="primary"
            />
            Apa itu Kategori?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Kategori adalah label pengelompokan untuk produk dan bahan baku Anda.
              Fungsinya seperti rak di toko fisik — setiap rak punya label sendiri
              sehingga barang mudah ditemukan.
            </p>
            <p class="text-body-2 mb-3">
              Di kasir PintuKasir, kategori muncul sebagai tombol filter di bagian atas
              daftar produk. Kasir bisa tap kategori "Minuman Panas" untuk langsung melihat
              semua produk minuman panas — tanpa perlu scroll panjang.
            </p>

            <VAlert
              type="success"
              variant="tonal"
              class="mb-0"
            >
              <strong>Contoh untuk coffee shop:</strong>
              Buat kategori Minuman Panas, Minuman Dingin, Makanan, dan Snack.
              Kasir tinggal tap kategori yang sesuai saat pelanggan pesan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Contoh Kategori -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-store-line"
              class="me-2"
              color="success"
            />
            Contoh Kategori untuk Coffee Shop
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Ini adalah kategori bawaan yang sudah disiapkan sistem untuk toko baru.
              Anda bisa langsung pakai atau sesuaikan dengan jenis usaha Anda.
            </p>

            <VTable>
              <thead>
                <tr>
                  <th>Warna</th>
                  <th>Nama Kategori</th>
                  <th>Contoh Produk</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="cat in exampleCategories"
                  :key="cat.name"
                >
                  <td>
                    <div
                      class="rounded"
                      :style="{
                        width: '24px',
                        height: '24px',
                        backgroundColor: cat.color,
                        display: 'inline-block',
                      }"
                    />
                  </td>
                  <td>
                    <div class="d-flex align-center gap-2">
                      <VIcon
                        :icon="cat.icon"
                        :style="{ color: cat.color }"
                        size="18"
                      />
                      <strong>{{ cat.name }}</strong>
                    </div>
                  </td>
                  <td class="text-body-2 text-medium-emphasis">
                    {{ cat.examples }}
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Tambah -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-add-circle-line"
              class="me-2"
              color="success"
            />
            Cara Menambah Kategori Baru
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Katalog → Kategori</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Tambah Kategori"</strong> di pojok kanan atas tabel
              </li>
              <li class="mb-3">
                Sebuah <strong>form dialog</strong> akan muncul. Isi field berikut:
                <VTable class="mt-2 mb-2">
                  <thead>
                    <tr>
                      <th style="min-width: 140px">
                        Field
                      </th>
                      <th style="min-width: 80px">
                        Wajib?
                      </th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Nama Kategori</strong>
                      </td>
                      <td>
                        <VChip
                          color="error"
                          size="x-small"
                          variant="tonal"
                        >
                          ✅ Wajib
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Nama kategori yang jelas dan mudah diingat.
                        Contoh: <strong>"Minuman Panas"</strong>, <strong>"Makanan Berat"</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Icon</strong>
                      </td>
                      <td>
                        <VChip
                          color="default"
                          size="x-small"
                          variant="tonal"
                        >
                          Opsional
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Ikon yang muncul di sebelah nama kategori.
                        Isi kode ikon Remix Icon, contoh: <code>ri-cup-line</code>,
                        <code>ri-restaurant-line</code>.
                        <br>
                        <span class="text-medium-emphasis">
                          Boleh dikosongkan jika tidak butuh ikon.
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Urutan Tampil</strong>
                      </td>
                      <td>
                        <VChip
                          color="default"
                          size="x-small"
                          variant="tonal"
                        >
                          Opsional
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Angka untuk mengatur posisi tampil. Angka lebih kecil = tampil lebih dulu.
                        <br>
                        Contoh: isi <strong>1</strong> agar kategori ini tampil paling pertama.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Warna Kategori</strong>
                      </td>
                      <td>
                        <VChip
                          color="default"
                          size="x-small"
                          variant="tonal"
                        >
                          Opsional
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Pilih dari 14 warna preset yang tersedia, atau ketik kode warna hex
                        sendiri (contoh: <code>#FF5733</code>).
                        <br>
                        <span class="text-medium-emphasis">
                          Warna ini muncul sebagai garis vertikal di kiri baris tabel.
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Status Aktif</strong>
                      </td>
                      <td>
                        <VChip
                          color="default"
                          size="x-small"
                          variant="tonal"
                        >
                          Default: Aktif
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Aktif = kategori muncul di kasir dan daftar produk.
                        <br>
                        Nonaktif = kategori tersembunyi (data tidak dihapus).
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-3">
                Klik tombol <strong>"Simpan"</strong>
              </li>
              <li class="mb-0">
                Kategori baru langsung muncul di tabel. ✅
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Edit & Hapus -->
    <VRow class="mt-4">
      <VCol
        cols="12"
        md="6"
      >
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-edit-line"
              class="me-2"
              color="warning"
            />
            Cara Edit Kategori
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                Temukan kategori di tabel, klik ikon <strong>pensil (✏️)</strong>
                di kolom Aksi
              </li>
              <li class="mb-2">
                Form edit terbuka — ubah field yang perlu diubah
              </li>
              <li class="mb-2">
                Klik <strong>"Update"</strong>
              </li>
              <li class="mb-0">
                Perubahan langsung berlaku di semua produk yang menggunakan kategori ini ✅
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="6"
      >
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-delete-bin-line"
              class="me-2"
              color="error"
            />
            Cara Hapus Kategori
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                Klik ikon <strong>tempat sampah (🗑️)</strong> di kolom Aksi
              </li>
              <li class="mb-2">
                Dialog konfirmasi muncul — klik <strong>"Hapus"</strong> untuk lanjut
              </li>
              <li class="mb-0">
                <VAlert
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  <strong>Tidak bisa dihapus</strong> jika masih ada produk atau
                  bahan baku yang menggunakan kategori ini.
                  Solusi: nonaktifkan saja (toggle Status menjadi Nonaktif).
                </VAlert>
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Filter & Pencarian -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard variant="outlined">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-search-line"
              class="me-2"
              color="info"
            />
            Filter & Pencarian di Halaman Kategori
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Di bagian atas tabel kategori, ada dua alat bantu:
            </p>
            <VList density="compact">
              <VListItem prepend-icon="ri-search-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Kotak Pencarian</strong> — Ketik nama kategori untuk langsung
                  menyaring tabel. Berguna saat sudah punya banyak kategori.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-checkbox-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Centang "Aktif saja"</strong> — Tampilkan hanya kategori yang
                  statusnya Aktif. Gunakan ini untuk menyembunyikan kategori yang sudah
                  dinonaktifkan.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Peringatan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert
          type="warning"
          variant="tonal"
          icon="ri-alert-line"
        >
          <p class="font-weight-bold mb-2">
            ⚠️ Hal yang Perlu Diperhatikan
          </p>
          <ul class="text-body-2 ps-4 mb-0">
            <li class="mb-1">
              Nama kategori <strong>harus unik</strong> — tidak boleh ada dua kategori
              dengan nama yang sama di toko Anda.
            </li>
            <li class="mb-1">
              Kategori yang dinonaktifkan <strong>tidak akan muncul di kasir</strong>,
              tapi data produknya tetap tersimpan.
            </li>
            <li class="mb-0">
              Jika mengganti nama kategori, nama baru akan langsung muncul di semua
              halaman yang menggunakan kategori tersebut.
            </li>
          </ul>
        </VAlert>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Pengaturan Umum', sub: 'Konfigurasi pajak & struk', icon: 'ri-equalizer-line', color: 'success', to: '/userguide/sistem/pengaturan' },
              { title: 'Satuan', sub: 'Satuan ukuran bahan baku', icon: 'ri-scales-2-line', color: 'warning', to: '/userguide/produk-inventory/satuan' },
              { title: 'Tambah Produk', sub: 'Gunakan kategori saat input produk', icon: 'ri-cup-line', color: 'primary', to: '/userguide/produk-inventory/produk' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
